import { RefreshCw, Loader2, AlertCircle, X } from 'lucide-react';
import type { GeneratedImage, GenerationMode } from './types';
import { LazyImage } from '../../components/LazyImage';

interface RecentWorksProps {
  images: GeneratedImage[];
  onSelect: (image: GeneratedImage) => void;
  onRefresh: () => void;
  onDelete?: (id: string) => void;
}

export function RecentWorks({ images, onSelect, onRefresh, onDelete }: RecentWorksProps) {
  const getModeLabel = (mode?: GenerationMode) => {
    switch (mode) {
      case 'img2img': return '参考图';
      case 'inpaint': return '局部重绘';
      default: return '文生图';
    }
  };

  const getModeColor = (mode?: GenerationMode) => {
    switch (mode) {
      case 'img2img': return 'bg-purple-100 text-purple-700';
      case 'inpaint': return 'bg-orange-100 text-orange-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="card mt-6 p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-light-text">最近作品</span>
        <button onClick={onRefresh} className="btn-secondary text-sm flex items-center gap-1">
          <RefreshCw className="w-3 h-3" />
          刷新
        </button>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-8 text-light-muted">
          暂无作品
        </div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {images.map((img) => (
            <div
              key={img.id}
              className="aspect-square rounded-xl overflow-hidden border border-light-border shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-200 relative group"
            >
              <button
                onClick={() => onSelect(img)}
                className="w-full h-full"
              >
                {img.status === 'processing' || img.status === 'pending' || img.status === 'submitting' ? (
                  <div className="w-full h-full bg-light-hover flex flex-col items-center justify-center">
                    <Loader2 className="w-6 h-6 text-accent-blue animate-spin mb-1" />
                    <span className="text-xs text-light-muted">生成中</span>
                  </div>
                ) : img.status === 'failed' ? (
                  <div className="w-full h-full bg-red-50 flex flex-col items-center justify-center px-1 text-center">
                    <AlertCircle className="w-6 h-6 text-red-400 mb-1" />
                    <span className="text-xs text-red-400 leading-tight">失败</span>
                    <span className="text-[10px] text-red-400/80 leading-tight">积分已退回</span>
                  </div>
                ) : img.url ? (
                  <>
                    <LazyImage
                      work={{ resultUrl: img.url }}
                      size="small"
                      alt={img.prompt}
                      className="w-full h-full object-cover"
                    />
                    <span className={`absolute top-1 left-1 text-[8px] px-1.5 py-0.5 rounded ${getModeColor(img.mode)}`}>
                      {getModeLabel(img.mode)}
                    </span>
                  </>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center">
                    <span className="text-xs text-gray-400">无图片</span>
                  </div>
                )}
              </button>
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm('确定要删除这张图片吗？')) {
                      onDelete(img.id);
                    }
                  }}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  title="删除图片"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
